import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Form, InputGroup, Container, Row, Col, Button, Modal, Badge, Toast, OverlayTrigger, Tooltip, Spinner, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faPlus, faFileExcel, faSortUp, faSortDown, faCheck, faBoxes, faClock, faExclamationTriangle, faChartLine, faSearch, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import api, { getInventoryByLot, getInventoryTotals, API_URL } from '../services/api';
import ShoppingCart from './ShoppingCart';
import Checkout from './Checkout';
import TempHistoryModal from './TempHistoryModal';
import './Inventory.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

console.log('API_URL:', API_URL);

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeout = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(1); // Ref so observer callback always sees latest page without re-registering
  const [totalItems, setTotalItems] = useState(0);
  const [globalTotals, setGlobalTotals] = useState({ lotCount: 0, uoh: 0, available: 0, tap: 0 });
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = sessionStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from sessionStorage:', error);
      return [];
    }
  });
  const [showCart, setShowCart] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerId, setCustomerId] = useState(null); // You'll need to set this when the user logs in or selects a customer
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  
  // Check if user can submit orders (submit or approve permission)
  // Note: 'submit' permission check returns true for both 'submit' and 'approve' due to hierarchy
  const canSubmitOrders = hasPermission('inventory', 'submit');
  const [pendingOrdersCache, setPendingOrdersCache] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const observerTarget = useRef(null);
  const [scrollBodyEl, setScrollBodyEl] = useState(null);
  const isLoadingRef = useRef(false); // Synchronous guard to prevent concurrent fetches
  const [kpis, setKpis] = useState(null);
  const [showInventoryWarning, setShowInventoryWarning] = useState(false);
  const [inventoryWarningItems, setInventoryWarningItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [tempModal, setTempModal] = useState({ show: false, idLevels: null, lotId: null });

  // Add columns array at component level - hide actions column for read-only users
  const columns = ['idLevels', 'itemNumber', 'itemName', 'notes', 'dims', 'weight', 'warehouseName', 'recDate', 'expDate', 'ppu', 'tap', 'uoh', 'availQty', ...(canSubmitOrders ? ['actions'] : [])];

  // Add mobile column configuration
  const mobileColumns = ['idLevels', 'itemNumber', 'itemName', 'availQty', 'actions'];
  
  // Function to get column classes for responsive design
  const getColumnClasses = (column) => {
    const classes = [];
    
    // Hide less important columns on mobile
    if (!mobileColumns.includes(column)) {
      classes.push('d-none', 'd-lg-table-cell');
    }
    
    // Right align numeric columns
    if (['ppu', 'tap', 'uoh', 'availQty'].includes(column)) {
      classes.push('text-end');
    }
    
    return classes.join(' ');
  };

  // Add headerStyle object at component level
  const headerStyle = {
    cursor: 'pointer',
    backgroundColor: '#f8f9fa',
    position: 'sticky',
    top: 0,
    zIndex: 999,
    boxShadow: 'inset 0 1px 0 #dee2e6, inset 0 -1px 0 #dee2e6, 0 2px 4px rgba(0,0,0,0.1)',
    padding: '0.75rem',
    fontWeight: 600,
    color: '#495057',
    border: 'none'
  };

  // Add tooltips object at component level
  const tooltips = {
    ppu: 'Pieces Per Unit - Number of individual pieces in one unit',
    uoh: 'Units On Hand - Total number of units currently in stock',
    availQty: 'Available Units - Number of units available for ordering',
    tap: 'Total Available Pieces - Total number of individual pieces available (Available Units × Pieces Per Unit)',
    dims: 'Product dimensions: Length × Width × Height',
    weight: 'Unit weight'
  };

  // Add renderColumnHeader function at component level
  const renderColumnHeader = (column) => {
    const headerContent = (
      <div className="d-flex justify-content-between align-items-center" style={{
        justifyContent: ['ppu', 'tap', 'uoh', 'availQty'].includes(column) ? 'flex-end' : 'space-between'
      }}>
        <span style={{
          textAlign: ['ppu', 'tap', 'uoh', 'availQty'].includes(column) ? 'right' : 'left',
          width: '100%'
        }}>
          {column === 'availQty' ? 'Available' : 
           column === 'idLevels' ? 'Lot ID' :
           column === 'itemNumber' ? 'Item Number' :
           column === 'itemName' ? 'Item Name' :
           column === 'recDate' ? 'Rec Dt' :
           column === 'expDate' ? 'Exp Dt' :
           column === 'uoh' ? 'UOH' :
           column === 'ppu' ? 'PPU' :
           column === 'tap' ? 'TAP' :
           column === 'notes' ? 'Notes' :
           column === 'dims' ? 'Dims (L×W×H)' :
           column === 'weight' ? 'Wt' :
           column === 'warehouseName' ? 'Warehouse' :
           column}
        </span>
        {column !== 'actions' && (
          <span className="ms-2">
            {sortConfig.key === column && (
              <FontAwesomeIcon 
                icon={sortConfig.direction === 'ascending' ? faSortUp : faSortDown}
                className="ms-1"
              />
            )}
          </span>
        )}
      </div>
    );

    if (tooltips[column]) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{tooltips[column]}</Tooltip>}
        >
          {headerContent}
        </OverlayTrigger>
      );
    }

    return headerContent;
  };

  // Define all callback functions first, before any useEffect hooks
  const fetchGlobalTotals = useCallback(async () => {
    try {
      const searchParam = searchTerm?.trim() || '';
      const response = await api.get('/api/inventory/lots/totals', {
        params: searchParam ? { search: searchParam } : {}
      });
      
      console.log('Fetched global totals:', response.data);
      setGlobalTotals(response.data);
    } catch (error) {
      console.error('Error fetching totals:', error);
      setError('Failed to fetch inventory totals');
    }
  }, [searchTerm]);

  const fetchInventory = useCallback(async (page = 1, resetData = false) => {
    try {
      if (!resetData && isLoadingRef.current) return; // Synchronous guard — prevents race condition
      isLoadingRef.current = true;

      if (resetData) {
        setLoading(true);
        setInventory([]); // Clear existing inventory when resetting
        setCurrentPage(1);
        currentPageRef.current = 1;
        setHasMore(false);
      }
      
      if (!resetData) {
        setIsLoading(true);
      }

      console.log('Fetching page:', page, 'resetData:', resetData, 'search:', searchTerm);

      const response = await getInventoryByLot(
        page,
        100,
        searchTerm,
        sortConfig.key,
        sortConfig.direction,
        false
      );

      if (response.items) {
        if (resetData) {
          setInventory(response.items);
          setFilteredInventory(response.items);
        } else {
          // Filter out any duplicates when adding new items
          setInventory(prevInventory => {
            const newInventory = [...prevInventory];
            response.items.forEach(item => {
              if (!newInventory.some(existing => existing.idLevels === item.idLevels)) {
                newInventory.push(item);
              }
            });
            return newInventory;
          });
          setFilteredInventory(prevInventory => {
            const newInventory = [...prevInventory];
            response.items.forEach(item => {
              if (!newInventory.some(existing => existing.idLevels === item.idLevels)) {
                newInventory.push(item);
              }
            });
            return newInventory;
          });
        }
        
        setHasMore(response.pagination?.hasMore || false);
        const newPage = response.pagination?.currentPage || page;
        setCurrentPage(newPage);
        currentPageRef.current = newPage;
        setTotalItems(response.pagination?.total || 0);

        console.log('Fetched inventory:', {
          page,
          hasMore: response.pagination?.hasMore,
          itemsCount: response.items?.length,
          totalItems: response.pagination?.total,
          currentInventory: response.items?.length
        });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError('Failed to fetch inventory data');
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      if (resetData) {
        setLoading(false);
      }
      setIsLoading(false);
    }
  }, [searchTerm, sortConfig.key, sortConfig.direction]);

  const fetchKpis = useCallback(async () => {
    try {
      const response = await api.get('/api/inventory/kpis');
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      setError('Failed to load inventory KPIs');
    }
  }, []);

  const fetchNextPage = useCallback(() => {
    if (hasMore && !loading && !isLoading) {
      console.log('Fetching next page:', currentPage + 1);
      setIsLoading(true);
      fetchInventory(currentPage + 1, false);
    }
  }, [hasMore, loading, isLoading, currentPage]);

  const safeUpdateCart = useCallback((updater) => {
    setCart(prevCart => {
      const newCart = typeof updater === 'function' ? updater(prevCart) : updater;
      try {
        console.log('Cart update - Previous cart:', prevCart);
        console.log('Cart update - New cart:', newCart);
        sessionStorage.setItem('cart', JSON.stringify(newCart));
      } catch (error) {
        console.error('Error saving cart to sessionStorage:', error);
      }
      return newCart;
    });
  }, []);

  const sortInventory = useCallback((data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';

      // Convert to numbers for numeric columns
      if (['onHand', 'availQty'].includes(sortConfig.key)) {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      // Handle dates
      if (['recDate', 'expDate'].includes(sortConfig.key)) {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [sortConfig]);

  const requestSort = useCallback((key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    // Reset pagination state and fetch with new sort
    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMore(true);
    setIsLoading(false);
    fetchInventory(1, true, key, direction);
  }, [sortConfig.key, sortConfig.direction]);

  // Initial load effect
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      // Initial load without search term
      const initialLoad = async () => {
        try {
          setLoading(true);
          
          // Load inventory
          const response = await getInventoryByLot(1, 100, '', 'itemNumber', 'ascending', false);
          if (response.items) {
            setInventory(response.items);
            setFilteredInventory(response.items);
            setHasMore(response.pagination?.hasMore || false);
            setCurrentPage(response.pagination?.currentPage || 1);
            currentPageRef.current = response.pagination?.currentPage || 1;
            setTotalItems(response.pagination?.total || 0);
          }
          
          // Load totals
          const totalsResponse = await api.get('/api/inventory/lots/totals');
          setGlobalTotals(totalsResponse.data);
          
          // Load KPIs
          const kpisResponse = await api.get('/api/inventory/kpis');
          setKpis(kpisResponse.data);
          
        } catch (error) {
          console.error('Error in initial load:', error);
          setError('Failed to fetch inventory data');
        } finally {
          setLoading(false);
        }
      };
      
      initialLoad();
    }
  }, [user, navigate]);

  // Handle search input changes with improved debounce
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    
    // Clear the existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // If search is empty, fetch immediately
    if (!value.trim()) {
      setIsLoading(true);
      Promise.all([
        getInventoryByLot(1, 100, '', sortConfig.key, sortConfig.direction, false).then(response => {
          if (response.items) {
            setInventory(response.items);
            setFilteredInventory(response.items);
            setHasMore(response.pagination?.hasMore || false);
            setCurrentPage(response.pagination?.currentPage || 1);
            currentPageRef.current = response.pagination?.currentPage || 1;
            setTotalItems(response.pagination?.total || 0);
          }
        }),
        api.get('/api/inventory/lots/totals').then(response => setGlobalTotals(response.data))
      ]).finally(() => setIsLoading(false));
      return;
    }

    // Set a new timeout to fetch data for non-empty searches
    searchTimeout.current = setTimeout(() => {
      setIsLoading(true);
      Promise.all([
        getInventoryByLot(1, 100, value, sortConfig.key, sortConfig.direction, false).then(response => {
          if (response.items) {
            setInventory(response.items);
            setFilteredInventory(response.items);
            setHasMore(response.pagination?.hasMore || false);
            setCurrentPage(response.pagination?.currentPage || 1);
            currentPageRef.current = response.pagination?.currentPage || 1;
            setTotalItems(response.pagination?.total || 0);
          }
        }),
        api.get('/api/inventory/lots/totals', {
          params: { search: value }
        }).then(response => setGlobalTotals(response.data))
      ]).finally(() => setIsLoading(false));
    }, 1000); // 1000ms debounce for better UX
  }, [sortConfig.key, sortConfig.direction]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Intersection observer — only re-registers when hasMore/scrollBodyEl changes
  useEffect(() => {
    if (!hasMore || !scrollBodyEl) return;

    const options = {
      root: scrollBodyEl,
      rootMargin: '200px',
      threshold: 0
    };

    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoadingRef.current) {
        const nextPage = currentPageRef.current + 1;
        console.log('Loading next page:', nextPage);
        fetchInventory(nextPage, false);
      }
    }, options);

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, fetchInventory, scrollBodyEl]);

  // Validate cart quantities against inventory when inventory loads
  useEffect(() => {
    if (inventory.length > 0 && cart.length > 0) {
      let cartNeedsUpdate = false;
      const updatedCart = cart.map(cartItem => {
        const inventoryItem = inventory.find(item => item.idLevels === cartItem.idLevels);
        // Only modify cart items if we actually find them in current inventory
        if (inventoryItem) {
          if (cartItem.quantity > Number(inventoryItem.availQty)) {
            cartNeedsUpdate = true;
            return {
              ...cartItem,
              quantity: Number(inventoryItem.availQty),
              availQty: Number(inventoryItem.availQty)
            };
          }
          // Update availQty even if quantity hasn't changed
          const newAvailQty = Number(inventoryItem.availQty);
          if (cartItem.availQty !== newAvailQty) {
            cartNeedsUpdate = true;
            return {
              ...cartItem,
              availQty: newAvailQty
            };
          }
        }
        return cartItem;
      });

      if (cartNeedsUpdate) {
        safeUpdateCart(updatedCart);
        setToastMessage('Cart quantities have been updated to reflect available quantities');
        setShowToast(true);
      }
    }
  }, [inventory.length, cart.length]); // Use lengths instead of full arrays

  const getCartItemQuantity = (idLevels) => {
    if (!idLevels) return 0;
    const cartItem = cart.find(item => item.idLevels === idLevels);
    return cartItem ? cartItem.quantity : 0;
  };

  // Remove the totals calculation from useMemo since we're using global totals
  const cartTotals = React.useMemo(() => {
    return cart.reduce((acc, item) => ({
      inCart: acc.inCart + item.quantity
    }), { inCart: 0 });
  }, [cart]);

  const getTrueAvailableQty = (item) => {
    if (!item || !item.idLevels || !item.availQty) return 0;
    const cartQty = getCartItemQuantity(item.idLevels);
    return Math.max(0, Number(item.availQty) - cartQty);
  };

  const handleAddToCart = (item) => {
    const trueAvailable = getTrueAvailableQty(item);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      return;
    }

    if (trueAvailable === 1) {
      const existingItem = cart.find(cartItem => cartItem.idLevels === item.idLevels);
      
      if (existingItem) {
        safeUpdateCart(prevCart => prevCart.map(cartItem =>
          cartItem.idLevels === item.idLevels
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        ));
      } else {
        safeUpdateCart(prevCart => [...prevCart, {
          idLevels: item.idLevels,
          itemNumber: item.itemNumber,
          itemName: item.itemName,
          quantity: 1,
          idLoc: item.idLoc,
          dimLen: item.dimLen,
          dimWdt: item.dimWdt,
          dimHgt: item.dimHgt,
          weight: item.weight,
          notes: item.notes,
          availQty: item.availQty,
          ppu: item.ppu
        }]);
      }
      
      setToastMessage(`Added ${item.itemName} to cart`);
      setShowToast(true);
    } else {
      setSelectedItem(item);
      setQuantityToAdd(1);
      setShowQuantityModal(true);
    }
  };

  const handleQuantitySubmit = () => {
    if (!selectedItem) return;
    
    const trueAvailable = getTrueAvailableQty(selectedItem);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      setShowQuantityModal(false);
      return;
    }

    const quantityToAddValidated = Math.min(quantityToAdd, trueAvailable);
    
    const existingItem = cart.find(cartItem => cartItem.idLevels === selectedItem.idLevels);
    if (existingItem) {
      safeUpdateCart(prevCart => prevCart.map(cartItem =>
        cartItem.idLevels === selectedItem.idLevels
          ? { ...cartItem, quantity: cartItem.quantity + quantityToAddValidated }
          : cartItem
      ));
    } else {
      safeUpdateCart(prevCart => [...prevCart, {
        idLevels: selectedItem.idLevels,
        itemNumber: selectedItem.itemNumber,
        itemName: selectedItem.itemName,
        quantity: quantityToAddValidated,
        idLoc: selectedItem.idLoc,
        dimLen: selectedItem.dimLen,
        dimWdt: selectedItem.dimWdt,
        dimHgt: selectedItem.dimHgt,
        weight: selectedItem.weight,
        notes: selectedItem.notes,
        availQty: Number(selectedItem.availQty),
        ppu: selectedItem.ppu
      }]);
    }

    setToastMessage(`Added ${quantityToAddValidated} ${selectedItem.itemName}${quantityToAddValidated > 1 ? 's' : ''} to cart`);
    setShowToast(true);
    setShowQuantityModal(false);
  };

  const handleAddAll = () => {
    if (!selectedItem) return;
    
    const trueAvailable = getTrueAvailableQty(selectedItem);
    
    if (trueAvailable <= 0) {
      setToastMessage('No more items available');
      setShowToast(true);
      setShowQuantityModal(false);
      return;
    }
    
    const existingItem = cart.find(cartItem => cartItem.idLevels === selectedItem.idLevels);
    
    if (existingItem) {
      safeUpdateCart(prevCart => prevCart.map(cartItem =>
        cartItem.idLevels === selectedItem.idLevels
          ? { ...cartItem, quantity: cartItem.quantity + trueAvailable }
          : cartItem
      ));
    } else {
      safeUpdateCart(prevCart => [...prevCart, {
        idLevels: selectedItem.idLevels,
        itemNumber: selectedItem.itemNumber,
        itemName: selectedItem.itemName,
        quantity: trueAvailable,
        idLoc: selectedItem.idLoc,
        dimLen: selectedItem.dimLen,
        dimWdt: selectedItem.dimWdt,
        dimHgt: selectedItem.dimHgt,
        weight: selectedItem.weight,
        notes: selectedItem.notes,
        availQty: Number(selectedItem.availQty),
        ppu: selectedItem.ppu
      }]);
    }

    setToastMessage(`Added all ${trueAvailable} ${selectedItem.itemName}${trueAvailable > 1 ? 's' : ''} to cart`);
    setShowToast(true);
    setShowQuantityModal(false);
  };

  const handleCheckoutComplete = async (checkoutData) => {
    try {
      const now = new Date();
      const mysqlDatetime = now.toISOString().slice(0, 19).replace('T', ' ');

      if (!user?.customerId) {
        throw new Error('No customer ID found. Please log in again.');
      }

      const selectedRateId = checkoutData.selectedRate;
      const shippingRates = JSON.parse(sessionStorage.getItem('shippingRates') || '[]');
      const selectedRate = shippingRates.find(rate => rate.rate_id === selectedRateId);

      const orderData = {
        header: {
          idCustomer: user.customerId,
          idClient: user.idClient,
          transDate: mysqlDatetime,
          transtype: 4,
          sGuid: window.crypto.randomUUID(),
          CustomerRef: checkoutData.customerRef || '',
          delName: checkoutData.shippingDetails?.name || '',
          delAddress: checkoutData.shippingDetails?.address1 || '',
          delCity: checkoutData.shippingDetails?.city || '',
          delState: checkoutData.shippingDetails?.state || '',
          delZip: checkoutData.shippingDetails?.zipCode || '',
          delPhone: checkoutData.shippingDetails?.phone || '',
          delNotes: checkoutData.shippingMethod === 'hold' ? checkoutData.holdInstructions : '',
          pickName: user.homeWarehouse?.name || '',
          pickAddress: user.homeWarehouse?.address?.street || '',
          pickCity: user.homeWarehouse?.address?.city || '',
          pickState: user.homeWarehouse?.address?.state || '',
          pickZip: user.homeWarehouse?.address?.zip || '',
          pickPhone: user.homeWarehouse?.address?.phone || '',
          status: 0,
          shipStat: 0,
          createDate: mysqlDatetime,
          shipDate: checkoutData.expectedShippingDate || null,
          shipping_service_code: selectedRate?.service_code || '',
          shipping_carrier_id: selectedRate?.carrier_id || '',
          shipping_rate_id: selectedRate?.rate_id || '',
          shipping_rate_amount: selectedRate?.shipping_amount?.amount || 0,
          shipping_service_type: selectedRate?.service_type || '',
          shipping_estimated_days: selectedRate?.delivery_days || 0,
          carrier: selectedRate?.carrier_friendly_name || '',
          shipping_id: selectedRate?.shipment_id || null
        },
        items: cart.map(item => ({
          recTrans: item.idLevels,
          transQty: item.quantity,
          notes: item.notes || '',
          idLoc: item.idLoc || null,
          dimLen: item.dimLen || null,
          dimWdt: item.dimWdt || null,
          dimHgt: item.dimHgt || null,
          weight: item.weight || null,
          shipping_id: selectedRate?.shipment_id || null
        }))
      };

      const response = await api.post('/api/inventory/pick-order', orderData);
      const result = response.data;
      console.log('Pick order created successfully:', result);

      if (result.success) {
        safeUpdateCart(() => []);
        
        // If order was submitted for approval, trigger notification banner refresh
        if (result.requiresApproval || result.approvalStatus === 'pending') {
          window.dispatchEvent(new Event('refreshPendingCount'));
        }
        
        navigate('/orders');
      } else {
        throw new Error(result.message || 'Failed to create pick order');
      }

    } catch (error) {
      console.error('Error creating pick order:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create pick order. Please try again.';
      alert(errorMessage);
      throw error;
    }
  };

  const fetchPendingOrders = async (lotId) => {
    try {
      const response = await api.get(`/api/inventory/pending-orders/${lotId}`);

      setPendingOrdersCache(prev => ({
        ...prev,
        [lotId]: response.data
      }));
    } catch (error) {
      console.error('Error in fetchPendingOrders:', error);
      setPendingOrdersCache(prev => ({
        ...prev,
        [lotId]: []
      }));
    }
  };

  const exportToExcel = async () => {
    try {
      setLoading(true);

      // Fetch the Excel file from the server
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/api/inventory/export', {
        params,
        responseType: 'blob'
      });

      // Get the blob from the response
      const blob = response.data;
      
      // Create a download link and trigger the download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'inventory_export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Update the footer to use global totals
  const footerCellStyle = {
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#495057',
    backgroundColor: '#f8f9fa',
    borderTop: '2px solid #dee2e6',
    padding: '0.6rem 0.75rem',
  };
  const footerLabelStyle = { fontSize: '0.7rem', fontWeight: 400, color: '#6c757d', display: 'block', textTransform: 'uppercase', letterSpacing: '0.03em' };

  const renderTableFooter = () => (
    <tfoot>
      <tr>
        {/* Lot ID — label + count */}
        <td style={footerCellStyle}>
          <span style={footerLabelStyle}>Total Lots</span>
          {globalTotals.lotCount?.toLocaleString() || 0}
        </td>
        {/* Item Number, Item Name, Notes, Dims, Weight, Warehouse, Received Date, Expiration Date — empty */}
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        <td style={footerCellStyle}></td>
        {/* PPU — empty */}
        <td style={footerCellStyle}></td>
        {/* TAP */}
        <td style={{ ...footerCellStyle, textAlign: 'right' }}>
          <span style={footerLabelStyle}>Total TAP</span>
          {globalTotals.tap?.toLocaleString() || 0}
        </td>
        {/* UOH */}
        <td style={{ ...footerCellStyle, textAlign: 'right' }}>
          <span style={footerLabelStyle}>Total UOH</span>
          {globalTotals.uoh?.toLocaleString() || 0}
        </td>
        {/* Available */}
        <td style={{ ...footerCellStyle, textAlign: 'right' }}>
          <span style={footerLabelStyle}>Total Available</span>
          {globalTotals.available?.toLocaleString() || 0}
        </td>
        {/* Actions — empty (only shown for submit-permission users) */}
        {canSubmitOrders && <td style={footerCellStyle}></td>}
      </tr>
    </tfoot>
  );

  const checkInventoryAvailability = async () => {
    try {
      // First check if we have a valid user
      if (!user) {
        setToastMessage('Your session has expired. Please log in again.');
        setShowToast(true);
        navigate('/login');
        return false;
      }

      // If cart is empty, no need to check
      if (cart.length === 0) {
        return true;
      }

      console.log('Checking inventory for cart items:', cart);

      // Create an array of objects with just the necessary information for validation
      const itemsToCheck = cart.map(item => ({
        idLevels: item.idLevels,
        requestedQuantity: parseInt(item.quantity, 10) || 0
      }));

      // Make a single POST request with all cart items to check
      const response = await api.post('/api/inventory/check-availability', { items: itemsToCheck });
      const result = response.data;
      console.log('Inventory check results:', result);

      // Expected response format: { success: boolean, unavailableItems: [] }
      if (result.success) {
        return true;
      }

      if (result.unavailableItems && result.unavailableItems.length > 0) {
        // Map server results back to full cart items with additional availability data
        const unavailableDetails = result.unavailableItems.map(serverItem => {
          const cartItem = cart.find(item => item.idLevels === serverItem.idLevels);
          return {
            ...cartItem,
            quantity: parseInt(cartItem.quantity, 10) || 0,
            latestAvailQty: parseInt(serverItem.availableQuantity, 10) || 0
          };
        });

        console.log('Unavailable items with details:', unavailableDetails);
        setInventoryWarningItems(unavailableDetails);
        setShowInventoryWarning(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking inventory:', error);
      if (error.message.includes('session')) {
        setToastMessage('Your session has expired. Please log in again.');
        setShowToast(true);
        navigate('/login');
      } else {
        setToastMessage('Failed to check inventory availability. Please try again.');
        setShowToast(true);
      }
      return false;
    }
  };

  // Add effect to handle tab/browser closing
  useEffect(() => {
    // Function to handle browser/tab closing
    const handleBeforeUnload = () => {
      // Don't clear the cart on page refreshes or navigation within the app
      // Only clear it when the tab/browser is actually closing
      const isActualClose = !document.hidden;
      if (isActualClose) {
        console.log('Browser tab/window closing, clearing cart');
        sessionStorage.removeItem('cart');
      }
    };

    // Add event listener for tab/browser close
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Removed early return - moved to conditional rendering in JSX

  const renderInventoryTable = () => {
    return (
      <div className="inventory-table-container">
        {/* Fixed Header */}
        <div className="inventory-table-header">
          <table className="inventory-table">
            <thead>
              <tr>
                {columns.map(column => (
                  <th 
                    key={column} 
                    onClick={() => column !== 'actions' && requestSort(column)}
                    className={getColumnClasses(column)}
                    style={{ 
                      cursor: column !== 'actions' ? 'pointer' : 'default',
                      backgroundColor: sortConfig.key === column ? '#e9ecef' : '#f8f9fa'
                    }}
                  >
                    {renderColumnHeader(column)}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable Body */}
        <div className="inventory-table-body" ref={setScrollBodyEl}>
          <table className="inventory-table">
            <tbody>
              {filteredInventory.map((item, idx) => (
                <tr 
                  key={`${item.idCatalog}-${item.idLevels}-${idx}`}
                >
                  {columns.map(column => (
                    <td 
                      key={`${item.idCatalog}-${item.idLevels}-${column}`}
                      className={getColumnClasses(column)}
                      style={{
                        whiteSpace: column === 'notes' ? 'normal' : 'nowrap'
                      }}
                    >
                      {column === 'actions' ? (
                        canSubmitOrders ? (
                        <div className="btn-group-vertical mobile-stack">
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => handleAddToCart(item)}
                            disabled={getCartItemQuantity(item.idLevels) >= Number(item.availQty)}
                            className="mobile-full-width"
                            style={{
                              padding: '0.25rem 0.4rem',
                              borderRadius: '4px',
                              transition: 'all 0.2s',
                              minHeight: '28px',
                              fontSize: '0.8rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            <FontAwesomeIcon icon={faShoppingCart} className="ms-1" />
                          </Button>
                        </div>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>Read Only</span>
                        )
                      ) : column === 'recDate' || column === 'expDate' ? formatDate(item[column]) :
                         column === 'itemNumber' ? (
                           <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                             {item[column].trim()}
                             {item.isTemp ? (
                               <button
                                 title="Temp controlled — click to view history"
                                 onClick={e => { e.stopPropagation(); setTempModal({ show: true, idLevels: item.idLevels, lotId: item.idLevels }); }}
                                 style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                               >🌡️</button>
                             ) : null}
                           </span>
                         ) :
                         column === 'availQty' ? renderAvailableQty(item) :
                         column === 'uoh' ? Number(item.onHand) :
                         column === 'ppu' ? Number(item.ppu || 1) :
                         column === 'tap' ? Number(item.availQty) * Number(item.ppu || 1) :
                         column === 'warehouseName' ? (item.warehouseName || 'N/A') :
                         column === 'dims' ? (
                           (item.dimLen || item.dimWdt || item.dimHgt)
                             ? `${item.dimLen ?? '—'}×${item.dimWdt ?? '—'}×${item.dimHgt ?? '—'}`
                             : '—'
                         ) :
                         column === 'weight' ? (item.weight != null ? item.weight : '—') :
                         column === 'notes' ? (item.notesDisplay || item.notes || '') :
                         item[column]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Infinite scroll sentinel — inside scrollable body so observer root works */}
          {hasMore && (
            <div
              ref={observerTarget}
              style={{ height: '40px' }}
            />
          )}
        </div>

        {/* Fixed Footer */}}
        <div className="inventory-table-footer">
          <table className="inventory-table">
            {renderTableFooter()}
          </table>
        </div>
      </div>
    );
  };

  // Render infinite scroll trigger separately
  const renderInfiniteScrollTrigger = () => {
    if (!hasMore) return null;
    return (
      <div 
        ref={observerTarget}
        style={{ height: '50px' }}
        className="w-100 d-flex justify-content-center align-items-center"
      >
        {isLoading && <Spinner animation="border" size="sm" />}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const renderAvailableQty = (item) => {
    if (!item) return null;
    const trueAvailable = getTrueAvailableQty(item);
    const cartQty = getCartItemQuantity(item.idLevels);
    
    return (
      <div className="available-qty">
        <span>{trueAvailable}</span>
        {cartQty > 0 && (
          <span className="text-muted" style={{ fontSize: '0.8em', marginLeft: '4px' }}>
            ({cartQty} in cart)
          </span>
        )}
      </div>
    );
  };

  const renderKpiCards = () => {
    if (!kpis) return null;

    const cards = [
      {
        title: 'Total SKUs',
        value: kpis.totalSKUs.toLocaleString(),
        icon: faBoxes,
        color: 'primary'
      },
      {
        title: 'Total Lots',
        value: kpis.totalLots.toLocaleString(),
        icon: faBoxes,
        color: 'info'
      },
      {
        title: 'Avg. Inventory Age',
        value: `${kpis.avgInventoryAge} days`,
        icon: faClock,
        color: 'warning'
      },
      {
        title: 'Expiring in 30 Days',
        value: kpis.expiringLots30Days.toLocaleString(),
        icon: faExclamationTriangle,
        color: 'danger'
      }
    ];

    return (
      <Row className="justify-content-center g-1 mx-0 mb-0 kpi-cards">
        {cards.map((card, index) => (
          <Col key={index} xs={6} sm={3} lg="auto" className="px-1">
            <Card className="shadow-sm kpi-card" style={{ minWidth: '120px' }}>
              <Card.Body className="text-center p-2 mobile-p-2">
                <div className="d-flex flex-column align-items-center">
                  <FontAwesomeIcon 
                    icon={card.icon} 
                    size="lg" 
                    className={`text-${card.color} mb-1`} 
                  />
                  <div className="text-muted small mobile-text-xs">{card.title}</div>
                  <div className="h6 mb-0 mobile-text-sm">{card.value}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Update the search input to show proper loading state
  const renderSearchInput = () => (
    <InputGroup size="sm">
      <InputGroup.Text className="bg-light border-end-0">
        <FontAwesomeIcon icon={faSearch} className="text-muted" />
      </InputGroup.Text>
      <Form.Control
        type="text"
        placeholder="Search items (item # and name start with, lot ID and notes contain)"
        value={searchTerm}
        onChange={(e) => handleSearch(e.target.value)}
        className="border-start-0 border-end-0"
        style={{ 
          boxShadow: 'none',
          borderLeft: 'none',
          borderRight: 'none'
        }}
      />
      {(searchTerm || isLoading) && (
        <InputGroup.Text className="bg-light border-start-0">
          {isLoading ? (
            <Spinner animation="border" size="sm" className="text-primary" />
          ) : searchTerm ? (
            <Button 
              variant="link" 
              onClick={() => handleSearch('')}
              size="sm"
              className="p-0 text-muted"
              style={{ 
                border: 'none', 
                background: 'none',
                fontSize: '16px',
                lineHeight: 1
              }}
            >
              ×
            </Button>
          ) : null}
        </InputGroup.Text>
      )}
    </InputGroup>
  );

  // Add CSS to prevent body scrolling
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // If checkout is active, render only the checkout component
  if (showCheckout) {
    return (
      <Checkout 
        cart={cart} 
        user={user}
        onCheckoutComplete={handleCheckoutComplete}
        onCancel={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <>
      <style>
        {`
          .inventory-table-container {
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
          }
          
          .inventory-table {
            table-layout: fixed;
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          
          .inventory-table th:nth-child(1),
          .inventory-table td:nth-child(1) { width: 6%; }   /* Lot ID */
          .inventory-table th:nth-child(2),
          .inventory-table td:nth-child(2) { width: 9%; }   /* Item Number */
          .inventory-table th:nth-child(3),
          .inventory-table td:nth-child(3) { width: 15%; }  /* Item Name */
          .inventory-table th:nth-child(4),
          .inventory-table td:nth-child(4) { width: 10%; }  /* Notes */
          .inventory-table th:nth-child(5),
          .inventory-table td:nth-child(5) { width: 8%; }   /* Dims */
          .inventory-table th:nth-child(6),
          .inventory-table td:nth-child(6) { width: 5%; }   /* Wt */
          .inventory-table th:nth-child(7),
          .inventory-table td:nth-child(7) { width: 9%; }   /* Warehouse */
          .inventory-table th:nth-child(8),
          .inventory-table td:nth-child(8) { width: 7%; }   /* Rec Dt */
          .inventory-table th:nth-child(9),
          .inventory-table td:nth-child(9) { width: 7%; }   /* Exp Dt */
          .inventory-table th:nth-child(10),
          .inventory-table td:nth-child(10) { width: 4%; }  /* PPU */
          .inventory-table th:nth-child(11),
          .inventory-table td:nth-child(11) { width: 5%; }  /* TAP */
          .inventory-table th:nth-child(12),
          .inventory-table td:nth-child(12) { width: 4%; }  /* UOH */
          .inventory-table th:nth-child(13),
          .inventory-table td:nth-child(13) { width: 7%; }  /* Available */
          .inventory-table th:nth-child(14),
          .inventory-table td:nth-child(14) { width: 6%; }  /* Actions */
          
          .inventory-table th,
          .inventory-table td {
            padding: 0.5rem !important;
            line-height: 1.3 !important;
            vertical-align: middle !important;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            border: 1px solid #dee2e6;
          }
          
          .inventory-table-header {
            flex-shrink: 0;
            overflow: hidden;
          }
          
          .inventory-table-header th {
            background-color: #f8f9fa !important;
            font-weight: 600 !important;
            color: #495057 !important;
            border-bottom: 2px solid #adb5bd !important;
          }
          
          .inventory-table-body {
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
          }
          
          .inventory-table-body tr {
            font-size: 0.9rem;
          }
          
          .inventory-table-body td {
            background-color: white;
          }
          
          .inventory-table-footer {
            flex-shrink: 0;
            overflow: hidden;
          }
          
          .inventory-table-footer td {
            background-color: #f8f9fa !important;
            font-weight: bold !important;
            color: #495057 !important;
            border-top: 2px solid #adb5bd !important;
            padding: 0.5rem !important;
          }
        `}
      </style>
      <Container fluid style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '0.5rem' }}>
        {error && (
          <Alert variant="danger" className="mb-2">
            {error}
          </Alert>
        )}
        <Row className="align-items-center mb-2">
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <h2 className="mb-0 mobile-center">Inventory</h2>
          </Col>
          <Col xs={12} md={8} className="mb-2 mb-md-0">
            {isMobile ? null : renderKpiCards()}
          </Col>
          <Col xs={12} md={2}></Col>
        </Row>

        <Row className="mb-2">
          <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
            {renderSearchInput()}
          </Col>
          <Col xs={12} md={6} lg={8} className="text-start text-md-end">
            <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end">
              <Button 
                variant="success"
                size="sm"
                onClick={exportToExcel}
                className="mobile-full-width"
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                Export to Excel
              </Button>
              {canSubmitOrders && (
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => setShowCart(true)}
                className="mobile-full-width"
              >
                <FontAwesomeIcon icon={faShoppingCart} />
                <Badge bg="secondary" className="ms-2">{cart.reduce((total, item) => total + item.quantity, 0)}</Badge>
              </Button>
              )}
            </div>
          </Col>
        </Row>
        
        <div style={{ 
          flex: 1,
          minHeight: 0,
          border: '1px solid #dee2e6', 
          borderRadius: '4px',
          backgroundColor: 'white',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {renderInventoryTable()}
        </div>
        
        {/* Quantity Modal */}
        <Modal show={showQuantityModal} onHide={() => setShowQuantityModal(false)} centered size="sm" dialogClassName="detail-modal">
          <Modal.Header closeButton>
            <div className="modal-title-row">
              <div className="modal-header-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
              <div className="modal-header-text">
                <h5>Add to Cart</h5>
                {selectedItem && <p className="modal-subtitle">{selectedItem.itemNumber}</p>}
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {selectedItem ? (
              <div>
                <div className="qty-modal-item-name">{selectedItem.itemName}</div>
                <div className="qty-modal-meta">Lot {selectedItem.idLevels} · {selectedItem.warehouseName || 'Warehouse'}</div>
                <div className="qty-modal-avail">
                  <FontAwesomeIcon icon={faCheck} />
                  {getTrueAvailableQty(selectedItem)} available
                </div>
                <div className="qty-modal-stepper">
                  <div className="qty-stepper">
                    <button className="qty-btn" onClick={() => setQuantityToAdd(q => Math.max(1, q - 1))} disabled={quantityToAdd <= 1}>
                      <FontAwesomeIcon icon={faSortDown} style={{ fontSize: '0.7rem' }} />
                    </button>
                    <input
                      type="number"
                      className="qty-input"
                      value={quantityToAdd}
                      onChange={(e) => setQuantityToAdd(Math.max(1, Math.min(Number(e.target.value), getTrueAvailableQty(selectedItem))))}
                      min={1}
                      max={getTrueAvailableQty(selectedItem)}
                    />
                    <button className="qty-btn" onClick={() => setQuantityToAdd(q => Math.min(q + 1, getTrueAvailableQty(selectedItem)))} disabled={quantityToAdd >= getTrueAvailableQty(selectedItem)}>
                      <FontAwesomeIcon icon={faSortUp} style={{ fontSize: '0.7rem' }} />
                    </button>
                  </div>
                  <span className="qty-modal-pieces">{quantityToAdd * Number(selectedItem.ppu || 1)} pcs · {Number(selectedItem.ppu || 1)} PPU</span>
                </div>
              </div>
            ) : <p>No item selected</p>}
          </Modal.Body>
          <Modal.Footer style={{ gap: 8 }}>
            <button className="detail-btn detail-btn--ghost" onClick={() => setShowQuantityModal(false)}>Cancel</button>
            {selectedItem && (
              <>
                <button className="detail-btn detail-btn--secondary" onClick={handleAddAll}>
                  Add All ({getTrueAvailableQty(selectedItem)})
                </button>
                <button className="detail-btn detail-btn--primary" onClick={handleQuantitySubmit}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                  Add to Cart
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>

      <Modal
        show={showCart}
        onHide={() => setShowCart(false)}
        size="lg"
        centered
        dialogClassName="detail-modal"
      >
        <Modal.Header closeButton>
          <div className="modal-title-row">
            <div className="modal-header-icon">
              <FontAwesomeIcon icon={faShoppingCart} />
            </div>
            <div className="modal-header-text">
              <h5>Shopping Cart</h5>
              <p className="modal-subtitle">{cart.reduce((t, i) => t + i.quantity, 0)} units across {cart.length} item{cart.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <ShoppingCart cart={cart} setCart={setCart} />
        </Modal.Body>
        {cart.length > 0 && (
          <Modal.Footer style={{ gap: 8 }}>
            <button className="detail-btn detail-btn--ghost" onClick={() => setShowCart(false)}>
              Continue Shopping
            </button>
            <button
              className="detail-btn detail-btn--primary"
              onClick={async () => {
                const ok = await checkInventoryAvailability();
                if (ok) { setShowCart(false); setShowCheckout(true); }
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: 6 }} />
              Proceed to Checkout
            </button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal
        show={showInventoryWarning}
        onHide={() => setShowInventoryWarning(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Inventory Availability Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The following items are no longer available in the requested quantities:</p>
          <ul>
            {inventoryWarningItems.map(item => (
              <li key={item.idLevels}>
                {item.itemName} - Requested: {item.quantity}, Available: {item.latestAvailQty}
              </li>
            ))}
          </ul>
          <p>Would you like to update your cart with the available quantities?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInventoryWarning(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            // Update cart with available quantities
            const updatedCart = cart.map(cartItem => {
              const warningItem = inventoryWarningItems.find(item => item.idLevels === cartItem.idLevels);
              if (warningItem) {
                return { ...cartItem, quantity: warningItem.latestAvailQty };
              }
              return cartItem;
            });
            safeUpdateCart(updatedCart);
            setShowInventoryWarning(false);
          }}>
            Update Quantities
          </Button>
        </Modal.Footer>
      </Modal>

        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
          <Toast 
            show={showToast} 
            onClose={() => setShowToast(false)} 
            delay={3000} 
            autohide
            style={{ backgroundColor: '#28a745', color: 'white' }}
          >
            <Toast.Body className="d-flex align-items-center">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              {toastMessage}
            </Toast.Body>
          </Toast>
        </div>
      </Container>

      <TempHistoryModal
        show={tempModal.show}
        onHide={() => setTempModal({ show: false, idLevels: null, lotId: null })}
        idLevels={tempModal.idLevels}
        lotId={tempModal.lotId}
      />
    </>
  );
}

export default Inventory;
